
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, Sparkles, Zap, Image, Palette, ChevronsDown } from 'lucide-react';
import { usePublicModels } from '@/hooks/usePublicModels';

const categoryIcons = {
  'text-to-image': Image,
  'ai-art': Palette,
  'general': Sparkles,
  'fast': Zap,
};

const ModelsDropdown = () => {
  const { models, loading } = usePublicModels();

  // Group models by category and clean up display names
  const groupedModels = models.reduce((acc, model) => {
    const category = model.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    
    // Enhanced cleaning of model display name to remove all technical details
    const cleanDisplayName = model.display_name
      .replace(/\s*\(.*?\)/g, '') // Remove anything in parentheses
      .replace(/\s*-\s*(P1|P2|P3|P4|A4F|Via\s+provider\s+\d+)/gi, '') // Remove provider info
      .replace(/\s*-\s*Provider\s+\d+/gi, '') // Remove "Provider X" patterns
      .replace(/\s*-\s*A4F$/gi, '') // Remove trailing A4F
      .replace(/\s*via\s+provider\s+\d+/gi, '') // Remove "via provider X" patterns
      .replace(/\s*\|\s*Provider\s+\d+/gi, '') // Remove "| Provider X" patterns
      .replace(/\s*\[\s*P\d+\s*\]/gi, '') // Remove [P1], [P2] etc patterns
      .replace(/\s*\(\s*P\d+\s*\)/gi, '') // Remove (P1), (P2) etc patterns
      .replace(/\s+/g, ' ') // Clean up extra spaces
      .trim();
    
    acc[category].push({
      ...model,
      display_name: cleanDisplayName
    });
    return acc;
  }, {} as Record<string, typeof models>);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300 text-sm 2xl:text-base whitespace-nowrap flex items-center gap-1"
        >
          AI Models
          <ChevronDown className="w-3 h-3 2xl:w-4 2xl:h-4 transition-transform duration-200" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-80 bg-slate-900/95 backdrop-blur-xl border-white/20 text-white shadow-2xl"
        align="end"
      >
        <DropdownMenuLabel className="text-gray-300 font-semibold">
          Available AI Models
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/20" />
        
        <ScrollArea className="h-[400px]">
          {loading ? (
            <DropdownMenuItem disabled className="text-gray-400">
              Loading models...
            </DropdownMenuItem>
          ) : Object.keys(groupedModels).length === 0 ? (
            <DropdownMenuItem disabled className="text-gray-400">
              No models available
            </DropdownMenuItem>
          ) : (
            Object.entries(groupedModels).map(([category, categoryModels]) => {
              const IconComponent = categoryIcons[category as keyof typeof categoryIcons] || Sparkles;
              
              return (
                <div key={category}>
                  <DropdownMenuLabel className="text-blue-400 text-xs uppercase tracking-wide flex items-center gap-2 mt-2">
                    <IconComponent className="w-3 h-3" />
                    {category.replace('-', ' ')}
                  </DropdownMenuLabel>
                  {categoryModels.map((model) => (
                    <DropdownMenuItem 
                      key={model.id}
                      className="hover:bg-white/10 focus:bg-white/10 cursor-pointer group transition-colors duration-200"
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <div className="font-medium text-white group-hover:text-blue-300 transition-colors duration-200">
                          {model.display_name}
                        </div>
                        <div className="flex gap-2 text-xs text-gray-500 mt-1">
                          {model.supported_sizes?.slice(0, 2).map((size) => (
                            <span key={size} className="bg-white/10 px-2 py-0.5 rounded text-xs">
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              );
            })
          )}
        </ScrollArea>
        
        <div className="flex items-center justify-center py-2 border-t border-white/20 bg-slate-900/50">
          <ChevronsDown className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-400 ml-2">Scroll for more models</span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ModelsDropdown;
